// src/features/b1_metadata/new.tsx

import { Form } from "@/components/ui/form";
import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { Diff } from "@/features/FormCard/Diff";
import {
  AutocompleteAsyncField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { fieldKeyOptions } from "./filter";

const FormSchema = z.object({
  // Display field for autocomplete
  owner_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
          text: z.string(),
        }),
      ])
      .optional(),
  ),
  // ID field
  owner_id: z.coerce.number().min(1, "Owner is required"),
  owner_type: z.string().min(1, "Owner Type is required"),
  field_key: z.string().min(1, "Field Key is required"),
  field_value: z.string().min(1, "Field Value is required"),
});

export function B1MetadataEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "owner_name") {
        return {
          text: value,
          id: fetchedData.owner_id,
          name: value,
        };
      }
      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                B1Metadata {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: owner fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="owner_name"
                    label={t(`${table_name}.columns.owner_name`, {
                      ns: "tables",
                      defaultValue: "Owner",
                    })}
                    base_url="etl-quantilia/strategies"
                    column_name="name"
                    id_column="owner_id"
                    control={form.control}
                    isRequired
                  />
                  <InputField
                    name="owner_type"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Owner Type",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
                {/* Row 2: metadata */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="field_key"
                    label={t(`${table_name}.columns.field_key`, {
                      ns: "tables",
                      defaultValue: "Field Key",
                    })}
                    options={fieldKeyOptions}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="field_value"
                    label={t(`${table_name}.columns.field_value`, {
                      ns: "tables",
                      defaultValue: "Field Value",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["owner_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
